import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import {
	CATEGORY_Y_AXIS_WIDTH,
	NO_ANIMATION,
	WrappedCategoryTickY,
	chartHeightForRows,
} from "./chart-ticks";
import { COLORS } from "./utils";
import type { HocBaByMajor } from "@/lib/statistics-api.service";

interface HocBaMajorChartProps {
	/** `undefined` = lần tải đầu chưa xong. */
	rows: HocBaByMajor[] | undefined;
}

const TITLE = "Điểm học bạ tổ hợp theo ngành (thang 30)";

/**
 * Sắp ngành theo `avgScore` GIẢM DẦN — ngành có điểm học bạ tổ hợp cao nhất
 * lên đầu. Đây chỉ là thứ tự hiển thị, KHÔNG phải xếp hạng "chất lượng" ngành
 * (yêu cầu rõ trong pha 09: không gắn nhãn cảnh báo/chất lượng cho ngành nào).
 */
export function sortHocBaByMajor(rows: readonly HocBaByMajor[]): HocBaByMajor[] {
	return [...rows].sort((a, b) => b.avgScore - a.avgScore);
}

/** Nhãn ngành, kèm mã khi tên trùng với một ngành khác trong cùng danh sách. */
function labelFor(row: HocBaByMajor, duplicateNames: Set<string>): string {
	if (!row.majorName) return row.majorCode || "Ngành không xác định";
	return duplicateNames.has(row.majorName) ? `${row.majorName} (${row.majorCode})` : row.majorName;
}

/**
 * Định dạng giá trị hiện trong tooltip của biểu đồ.
 *
 * `null` (nhóm đã/chưa tiếp nhận rỗng) phải hiện thành "không có dữ liệu",
 * KHÔNG PHẢI "0" — một ngành chưa ai tiếp nhận không có nghĩa điểm TB của
 * nhóm đó bằng 0, nó đơn giản là không tồn tại để tính trung bình.
 */
export function formatHocBaTooltipValue(value: unknown): string {
	return typeof value === "number" ? value.toFixed(2) : "không có dữ liệu";
}

/**
 * Nhãn số ngay đầu mỗi thanh.
 *
 * KHÔNG dùng `BAR_VALUE_LABEL_RIGHT` dùng chung: formatter ở đó gom nhóm hàng
 * nghìn cho số đếm ("1.234"), còn đây là điểm thang 30 và phải giữ đúng 2 chữ
 * số thập phân như tooltip — hai chỗ hiện hai con số khác nhau cho cùng một
 * thanh là thứ người đọc không có cách nào tự giải thích.
 *
 * Cần nhãn vì trục ngang bị cắt (không bắt đầu từ 0): độ dài thanh không đọc
 * ra được giá trị, phải có con số đi kèm.
 */
const HOC_BA_BAR_LABEL = {
	position: "right",
	fontSize: 11,
	className: "fill-muted-foreground",
	formatter: (value: unknown) => (typeof value === "number" ? value.toFixed(2) : ""),
} as const;

/**
 * Điểm học bạ tổ hợp trung bình theo ngành, kèm so sánh nhóm ĐÃ tiếp nhận vs
 * CHƯA tiếp nhận trên cùng hàng.
 *
 * Đây là mục đích chính của biểu đồ: kiểm chứng ngành có ngưỡng phụ ngặt (VD
 * Công nghệ bán dẫn, Thiết kế vi mạch) có đang mất thí sinh điểm cao vào
 * trường khác hay không — nếu nhóm chưa tiếp nhận có điểm TB cao hơn nhóm đã
 * tiếp nhận, đó là dấu hiệu.
 *
 * `receivedAvgScore`/`notReceivedAvgScore` bằng `null` khi nhóm đó rỗng (ngành
 * chưa ai tiếp nhận, hoặc cả ngành đã tiếp nhận hết). Recharts tự bỏ qua giá
 * trị `null` khi vẽ Bar — không thanh nào được vẽ, đúng ý "không có dữ liệu"
 * chứ không phải một thanh cao 0.
 */
export function HocBaMajorChart({ rows }: HocBaMajorChartProps) {
	if (rows === undefined) {
		return (
			<ChartCard title={TITLE} filename="diem-hoc-ba-to-hop-theo-nganh">
				<Skeleton className="h-[360px] w-full" />
			</ChartCard>
		);
	}

	if (rows.length === 0) {
		return (
			<ChartCard title={TITLE} filename="diem-hoc-ba-to-hop-theo-nganh">
				<p className="py-8 text-center text-sm text-muted-foreground">
					Chưa có dữ liệu điểm học bạ tổ hợp theo ngành trong phạm vi đang lọc.
				</p>
			</ChartCard>
		);
	}

	const sorted = sortHocBaByMajor(rows);
	const nameCounts = new Map<string, number>();
	for (const row of rows) {
		if (row.majorName) nameCounts.set(row.majorName, (nameCounts.get(row.majorName) ?? 0) + 1);
	}
	const duplicateNames = new Set(
		[...nameCounts.entries()].filter(([, count]) => count > 1).map(([name]) => name),
	);

	// Một đại lượng duy nhất: tập hồ sơ của tab đã là "đã tiếp nhận hoặc đã
	// đóng học phí", nên "TB đã tiếp nhận" và "TB chung" sẽ là cùng một con số.
	const data = sorted.map((row) => ({
		majorLabel: labelFor(row, duplicateNames),
		"TB chung": row.avgScore,
	}));

	// Trục CẮT theo dải dữ liệu thật, không chạy từ 0.
	//
	// Điểm học bạ của cả khoá dồn trong khoảng 26-29 trên thang 30. Vẽ trục
	// 0->30 thì 39 ngành ra 39 thanh dài gần bằng nhau và không đọc được gì —
	// đúng thứ biểu đồ này sinh ra để trả lời lại là thứ nó giấu đi.
	//
	// Trục cắt vốn có thể phóng đại khác biệt, nên hai thứ phải đi kèm: mốc đầu
	// trục làm tròn xuống (không bám sát giá trị nhỏ nhất để khỏi thổi phồng),
	// và một dòng ghi rõ trục không bắt đầu từ 0 ngay dưới biểu đồ.
	const values = data.map((d) => d["TB chung"]).filter((v): v is number => typeof v === "number");
	const axisMin = values.length ? Math.max(0, Math.floor(Math.min(...values)) - 1) : 0;
	const axisMax = values.length ? Math.min(30, Math.ceil(Math.max(...values))) : 30;

	return (
		<ChartCard
			title={TITLE}
			filename="diem-hoc-ba-to-hop-theo-nganh"
			description="Sắp theo điểm học bạ tổ hợp trung bình giảm dần — không phải xếp hạng chất lượng ngành"
		>
			<ResponsiveContainer width="100%" height={chartHeightForRows(data.length)}>
				<BarChart
					layout="vertical"
					data={data}
					// right 48: chừa chỗ cho nhãn số nằm ngoài đầu thanh; ở 24 nhãn của
					// thanh dài nhất bị khung cắt mất một phần.
					margin={{ top: 10, right: 48, left: 8, bottom: 10 }}
				>
					<CartesianGrid strokeDasharray="3 3" />
					<XAxis type="number" domain={[axisMin, axisMax]} allowDecimals />
					<YAxis
						type="category"
						dataKey="majorLabel"
						width={CATEGORY_Y_AXIS_WIDTH}
						tick={<WrappedCategoryTickY />}
						interval={0}
					/>
					<Tooltip formatter={formatHocBaTooltipValue} />
					{/* Một đại lượng duy nhất -> bỏ Legend: một ô chú giải cho một màu
					    chỉ lặp lại đúng cái nhãn đã có trên tooltip và tiêu đề. */}
					<Bar
						{...NO_ANIMATION}
						dataKey="TB chung"
						fill={COLORS[0]}
						barSize={14}
						label={HOC_BA_BAR_LABEL}
					/>
				</BarChart>
			</ResponsiveContainer>

			<p className="mt-3 text-xs text-muted-foreground">
				Trục ngang bắt đầu từ {axisMin} chứ không từ 0 (thang đầy đủ là 30): điểm học bạ của cả khoá
				dồn trong một dải hẹp, vẽ từ 0 thì mọi ngành ra thanh dài gần bằng nhau. Đọc khoảng cách
				giữa các ngành theo đúng mốc trên trục, đừng đọc theo độ dài thanh.
			</p>
		</ChartCard>
	);
}
